import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Plus } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { Button } from "@/components/ui/primitives";
import { ConfirmDialog } from "@/components/ui";
import { ChecklistListPanel } from "./ChecklistListPanel";
import { ChecklistEditPanel } from "./ChecklistEditPanel";
import { ChecklistCreateDialog } from "./ChecklistCreateDialog";
import { useTranslation } from "@/packages/utils";
import type { ChecklistItemId, UIChecklistItem } from "@/packages/types";

interface ChecklistEditPageProps {
  auditId: Id<"audits">;
  selectedItemId?: string;
  statusFilter?: string;
  searchQuery?: string;
}

export function ChecklistEditPage({
  auditId,
  selectedItemId: initialItemId,
  statusFilter: initialStatusFilter,
  searchQuery: initialSearchQuery,
}: ChecklistEditPageProps) {
  const { t } = useTranslation(["audits", "common"]);
  const navigate = useNavigate();
  // workspaceSlug is available from any ancestor route param
  const { workspaceSlug } = useParams({ strict: false });

  const checklistData = useQuery(api.checklist_queries.getChecklistByAuditId, {
    auditId,
  });

  const [selectedId, setSelectedId] = useState<ChecklistItemId | null>(
    // Unavoidable cast: route param is a raw string; branded ChecklistItemId
    // is structurally identical at runtime (both are strings).
    (initialItemId as ChecklistItemId) ?? null,
  );
  const [statusFilter, setStatusFilter] = useState(
    initialStatusFilter ?? "all",
  );
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery ?? "");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingItemId, setPendingItemId] = useState<ChecklistItemId | null>(
    null,
  );
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);

  // Build all category groups (unfiltered) for progress tracking
  const allCategories = useMemo(() => {
    if (!checklistData) return [];

    return checklistData.categories.map((cat) => ({
      id: cat._id as string,
      name: cat.name,
      items: checklistData.items
        .filter((item) => item.categoryId === cat._id)
        .sort((a, b) => a.order - b.order)
        .map(
          (item): UIChecklistItem => ({
            // Unavoidable cast: Convex Id<"checklistItems"> -> branded ChecklistItemId.
            id: item._id as unknown as ChecklistItemId,
            name: item.description,
            description: item.description,
            status: item.status,
            required: item.required,
            isAnnex: item.isAnnex,
            notes: item.notes,
            clauseRefs: item.clauseRefs,
            questions: item.questions,
            answers: item.answers,
            categoryId: item.categoryId,
            deletedAt: item.deletedAt,
          }),
        ),
    }));
  }, [checklistData]);

  // Build filtered category groups
  const filteredCategories = useMemo(() => {
    if (!checklistData) return [];

    return checklistData.categories
      .map((cat) => ({
        // Id<"checklistCategories"> is a string at runtime; CategoryGroup.id is string
        id: cat._id as string,
        name: cat.name,
        items: checklistData.items
          .filter((item) => item.categoryId === cat._id)
          .filter((item) =>
            statusFilter === "all" ? true : item.status === statusFilter,
          )
          .filter((item) =>
            searchQuery
              ? item.description
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                item.clauseRefs?.some((ref) =>
                  ref.toLowerCase().includes(searchQuery.toLowerCase()),
                )
              : true,
          )
          .sort((a, b) => a.order - b.order)
          .map(
            (item): UIChecklistItem => ({
              // Unavoidable cast: Convex Id<"checklistItems"> -> branded ChecklistItemId.
              // Both are strings at runtime; this boundary is intentional.
              id: item._id as unknown as ChecklistItemId,
              name: item.description,
              description: item.description,
              status: item.status,
              required: item.required,
              isAnnex: item.isAnnex,
              notes: item.notes,
              clauseRefs: item.clauseRefs,
              questions: item.questions,
              answers: item.answers,
              categoryId: item.categoryId,
              deletedAt: item.deletedAt,
            }),
          ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [checklistData, statusFilter, searchQuery]);

  // Overall progress stats
  const progressStats = useMemo(() => {
    if (!checklistData)
      return { total: 0, approved: 0, uploaded: 0, missing: 0 };

    const items = checklistData.items;
    return {
      total: items.length,
      approved: items.filter((i) => i.status === "APPROVED").length,
      uploaded: items.filter((i) => i.status === "UPLOADED").length,
      missing: items.filter((i) => i.status === "MISSING").length,
    };
  }, [checklistData]);

  const statusCounts = useMemo(
    () => ({
      all: progressStats.total,
      missing: progressStats.missing,
      uploaded: progressStats.uploaded,
      approved: progressStats.approved,
    }),
    [progressStats],
  );

  // Auto-select first item if none selected
  useEffect(() => {
    if (!selectedId && filteredCategories.length > 0) {
      const firstItem = filteredCategories[0]?.items[0];
      if (firstItem) setSelectedId(firstItem.id);
    }
  }, [filteredCategories, selectedId]);

  // Find selected item
  const selectedItem = useMemo(() => {
    for (const cat of filteredCategories) {
      const found = cat.items.find((item) => item.id === selectedId);
      if (found) return found;
    }
    return null;
  }, [filteredCategories, selectedId]);

  const handleSelectItem = useCallback(
    (id: ChecklistItemId) => {
      if (isDirty && id !== selectedId) {
        setPendingItemId(id);
        setShowUnsavedPrompt(true);
        return;
      }
      setSelectedId(id);
    },
    [isDirty, selectedId],
  );

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleCreated = useCallback((itemId: string) => {
    // Unavoidable cast: onCreated callback uses string; branded type is string at runtime
    setSelectedId(itemId as ChecklistItemId);
  }, []);

  if (!checklistData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const auditName = checklistData.auditName;
  const progressPct =
    progressStats.total > 0
      ? Math.round((progressStats.approved / progressStats.total) * 100)
      : 0;

  return (
    <div className="flex h-full flex-col -mx-4 -mt-2 -mb-1">
      {/* Header with breadcrumb and progress */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2 shadow-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                to: "/$workspaceSlug/audits/$auditId",
                // Unavoidable cast: Id<"audits"> is a branded string; TanStack Router
                // expects a plain string for params.
                params: {
                  workspaceSlug: workspaceSlug ?? "",
                  auditId: auditId as string,
                },
                search: { tab: "checklist" },
              })
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {/* Breadcrumb */}
          <nav className="flex items-center gap-1 min-w-0 text-sm">
            <span className="truncate text-muted-foreground max-w-[200px]">
              {auditName}
            </span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-semibold truncate">
              {t("checklist.edit.pageTitle")}
            </span>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {t("checklist.edit.progress", {
                completed: progressStats.approved,
                total: progressStats.total,
              })}
            </span>
            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-green-500 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Add Item button */}
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => setShowCreateDialog(true)}
          >
            {t("checklist.edit.addItem")}
          </Button>
        </div>
      </div>

      {/* Split panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — 30% */}
        <div className="w-[40%] min-w-[360px] max-w-[520px] shrink-0">
          <ChecklistListPanel
            categories={filteredCategories}
            allCategories={allCategories}
            selectedItemId={selectedId}
            onSelectItem={handleSelectItem}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusCounts={statusCounts}
          />
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0">
          {selectedItem ? (
            <ChecklistEditPanel
              item={selectedItem}
              auditId={auditId}
              onDirtyChange={setIsDirty}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t("checklist.edit.selectItemPlaceholder")}
            </div>
          )}
        </div>
      </div>

      {/* Unsaved changes prompt */}
      <ConfirmDialog
        open={showUnsavedPrompt}
        onClose={() => {
          setShowUnsavedPrompt(false);
          setPendingItemId(null);
        }}
      >
        <ConfirmDialog.Title>
          {t("checklist.edit.unsavedPromptTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("checklist.edit.unsavedPromptDescription")}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={() => {
            setShowUnsavedPrompt(false);
            setIsDirty(false);
            if (pendingItemId) {
              setSelectedId(pendingItemId);
              setPendingItemId(null);
            }
          }}
          onCancel={() => {
            setShowUnsavedPrompt(false);
            setPendingItemId(null);
          }}
          confirmLabel={t("checklist.edit.discardChanges")}
          variant="danger"
        />
      </ConfirmDialog>

      {/* Create dialog */}
      <ChecklistCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        auditId={auditId}
        categories={checklistData.categories}
        onCreated={handleCreated}
      />
    </div>
  );
}
